import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import ButtonV2 from 'components/ButtonV2';
import {
  SearchCard,
  SearchContainer,
  SearchInput,
  SearchInputWrapper,
  ClearButton,
  FilterPills,
  MobileFilterPills,
  SearchCollapsed,
  SearchExpanded,
  SearchExpandedInput,
} from './StyledComponents';

const SearchSection = ({
  onSearch,
  onFilterChange,
  activeFilter: controlledActiveFilter,
  initialSearchTerm = '',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [internalActiveFilter, setInternalActiveFilter] = useState('All Users');
  const [isSearchExpanded, setIsSearchExpanded] = useState(
    Boolean(initialSearchTerm)
  );

  // Use controlled activeFilter if provided, otherwise use internal state
  const activeFilter =
    controlledActiveFilter !== undefined
      ? controlledActiveFilter
      : internalActiveFilter;
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const filters = [
    'All Users',
    'Pit Pay',
    'Staff',
    'Account Owner',
    'App User',
  ];

  // Handle clicks outside to collapse search
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchExpanded(false);
      }
    };

    if (isSearchExpanded) {
      document.addEventListener('click', handleClickOutside);
      // Focus the input when expanded
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isSearchExpanded]);

  // Trigger search on every change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, onSearch]);

  const handleFilterClick = (filter) => {
    // Only update internal state if not controlled
    if (controlledActiveFilter === undefined) {
      setInternalActiveFilter(filter);
    }
    if (onFilterChange) {
      onFilterChange(filter);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      // Just blur the input on Enter since search is automatic
      e.target.blur();
    }
  };

  const expandSearch = () => {
    setIsSearchExpanded(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    // Focus the appropriate input after clearing
    if (inputRef.current && isSearchExpanded) {
      inputRef.current.focus();
    }
  };

  return (
    <>
      {/* Mobile Expandable Search */}
      {!isSearchExpanded ? (
        <SearchCollapsed onClick={expandSearch}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <span>Tap to search users...</span>
        </SearchCollapsed>
      ) : (
        <SearchExpanded ref={searchRef}>
          <SearchExpandedInput
            ref={inputRef}
            type="text"
            placeholder="Search users by name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ paddingRight: searchTerm.length > 0 ? '40px' : '16px' }}
          />
          {searchTerm.length > 0 && (
            <ClearButton onClick={handleClear} aria-label="Clear search">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </ClearButton>
          )}
        </SearchExpanded>
      )}

      {/* Desktop Search Card */}
      <SearchCard>
        <SearchContainer>
          <SearchInputWrapper>
            <SearchInput
              type="text"
              placeholder="Search users by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{ paddingRight: searchTerm.length > 0 ? '40px' : '16px' }}
            />
            {searchTerm.length > 0 && (
              <ClearButton onClick={handleClear} aria-label="Clear search">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </ClearButton>
            )}
          </SearchInputWrapper>
        </SearchContainer>

        <FilterPills>
          {filters.map((filter) => (
            <ButtonV2
              key={filter}
              variant="pill"
              active={activeFilter === filter}
              onClick={() => handleFilterClick(filter)}
            >
              {filter}
            </ButtonV2>
          ))}
        </FilterPills>
      </SearchCard>

      {/* Mobile Filter Pills (always visible on mobile) */}
      <MobileFilterPills>
        {filters.map((filter) => (
          <ButtonV2
            key={`mobile-${filter}`}
            variant="pill"
            active={activeFilter === filter}
            onClick={() => handleFilterClick(filter)}
          >
            {filter}
          </ButtonV2>
        ))}
      </MobileFilterPills>
    </>
  );
};

SearchSection.propTypes = {
  onSearch: PropTypes.func,
  onFilterChange: PropTypes.func,
  activeFilter: PropTypes.string,
  initialSearchTerm: PropTypes.string,
};

export default SearchSection;
